import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/common/Header';
import LoadingScreen from './components/common/LoadingScreen';
import LandingPage from './components/common/LandingPage';
import RiderDashboard from './components/rider/RiderDashboard';
import DriverView from './components/driver/DriverView';
import AdminView from './components/admin/AdminView';
import DriverOnboarding from './components/driver/DriverOnboarding';
import PaymentCallback from './components/common/PaymentCallback';
import { GOOGLE_MAPS_API_KEY } from './utils/constants';
import './App.css';

// Main App Component
function AppContent({ onShowDriverOnboarding }) {
  const { user, isAuthenticated, loading } = useAuth();
  const [appLoading, setAppLoading] = useState(true);
  const [paymentRef, setPaymentRef] = useState(null);

  useEffect(() => {
    // Check if this is a Paystack payment callback
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('reference') || params.get('trxref');
    if (ref) setPaymentRef(ref);

    // Load Google Maps API
    if (!window.google && !window.googleMapsLoading) {
      loadGoogleMaps();
    }

    // App initialization delay
    setTimeout(() => setAppLoading(false), 1500);
  }, []);

  const loadGoogleMaps = () => {
    window.googleMapsLoading = true;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initMaps`;
    script.async = true;
    script.defer = true;
    
    // Create a global callback function
    window.initMaps = () => {
      window.googleMapsLoading = false;
      delete window.initMaps;
    };
    
    script.onerror = () => {
      console.error('Failed to load Google Maps');
      window.googleMapsLoading = false;
      delete window.initMaps; // Clean up
    };
    
    document.head.appendChild(script);
  };

  const handleLogin = (userData) => {
    // The AuthContext will be updated via the custom event
    // Just ensure we close any modals or update local state if needed
    console.log('Login successful for:', userData.name, userData.userType);
  };

  // Show loading screen during app initialization
  if (appLoading || loading) {
    return <LoadingScreen />;
  }

  // Handle Paystack payment callback
  if (paymentRef) {
    return <PaymentCallback reference={paymentRef} onDone={() => setPaymentRef(null)} />;
  }

  // Show landing page for non-authenticated users
  if (!isAuthenticated) {
    return <LandingPage onLogin={handleLogin} onShowDriverOnboarding={onShowDriverOnboarding} />;
  }

  // Show appropriate dashboard based on user type
  const renderUserDashboard = () => {
    switch (user?.userType) {
      case 'rider':
        return <RiderDashboard />;
      case 'driver':
        return <DriverView />;
      case 'admin':
        return <AdminView />;
      default:
        return (
          <div className="error-view">
            <h2>Access Error</h2>
            <p>Your account type is not recognized. Please contact support.</p>
          </div>
        );
    }
  };

  return (
    <div className="App">
      <Header />
      <main className="main-content">
        {renderUserDashboard()}
      </main>
    </div>
  );
}

// Root App Component with Providers
function App() {
  const [showDriverOnboarding, setShowDriverOnboarding] = useState(false);

  return (
    <AuthProvider>
      <div className="App">
        <AppContent onShowDriverOnboarding={() => setShowDriverOnboarding(true)} />
        {showDriverOnboarding && (
          <DriverOnboarding onClose={() => setShowDriverOnboarding(false)} />
        )}
      </div>
    </AuthProvider>
  );
}

export default App;